import { InputAdornment, TextField } from "@mui/material";
import type { FC, ReactNode } from "react";
import { memo, useCallback, useMemo } from "react";
import { toNumber } from "../../misc/utils";

interface Props {
  readonly value: number | null | undefined;
  readonly label?: string;
  readonly placeholder?: string;
  readonly size?: "small" | "medium";
  readonly fullWidth?: boolean;
  readonly autoFocus?: boolean;
  readonly disabled?: boolean;
  readonly readonly?: boolean;
  readonly testId?: string;
  readonly min?: number;
  readonly max?: number;
  readonly suffix?: ReactNode;
  readonly margin?: "dense" | "none" | "normal";
  readonly minWidth?: string | number;
  readonly error?: boolean;
  readonly helperText?: ReactNode;
  readonly onChange: (value: number | null) => void;
}

const NumberField: FC<Props> = memo(
  ({
    value,
    label,
    placeholder,
    size,
    fullWidth,
    disabled,
    readonly,
    autoFocus,
    testId,
    min,
    max,
    suffix,
    margin,
    minWidth,
    error,
    helperText,
    onChange,
  }: Props) => (
    <TextField
      size={size}
      label={label}
      placeholder={placeholder}
      type="number"
      margin={margin}
      fullWidth={fullWidth}
      autoFocus={autoFocus}
      sx={useMemo(() => ({ minWidth }), [minWidth])}
      value={value || value === 0 ? `${value}` : ""}
      disabled={disabled}
      data-testid={testId}
      helperText={helperText}
      error={error}
      onChange={useCallback((e) => onChange(toNumber(e.target.value)), [onChange])}
      slotProps={{
        input: useMemo(
          () => ({
            endAdornment: suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : undefined,
            readOnly: readonly,
          }),
          [readonly, suffix],
        ),

        htmlInput: useMemo(() => ({ min, max }), [min, max]),
      }}
    />
  ),
);
NumberField.displayName = "NumberField";

export default NumberField;
